import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Alert, Button, ConfirmModal, Modal, ModalFooter } from "@/components/ui";
import { RegionSelect } from "@/components/common/RegionSelect";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { formatReaderSerialNumber } from "@/lib/readerSerialNumber";
import { readerService } from "@/services/readerService";
import type { ReaderInventory } from "@/types";
import { ReaderStatus } from "@/types";

interface ChangeReaderRegionModalProps {
  isOpen: boolean;
  reader: ReaderInventory | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

export function ChangeReaderRegionModal({
  isOpen,
  reader,
  onClose,
  onChanged,
}: ChangeReaderRegionModalProps) {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const [regionId, setRegionId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unpairConfirmOpen, setUnpairConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !reader) return;
    setRegionId(reader.region_id);
    setError(null);
    setUnpairConfirmOpen(false);
  }, [isOpen, reader]);

  const willUnpair = reader
    ? reader.status === ReaderStatus.PAIRED ||
      Boolean(reader.external_reader_id?.trim())
    : false;
  const serialLabel = reader
    ? formatReaderSerialNumber(reader.serial_number, reader.provider)
    : "";

  const submitRegionChange = async () => {
    if (!reader) return;
    setUnpairConfirmOpen(false);
    setIsLoading(true);
    setError(null);
    try {
      await readerService.update(reader.id, { region_id: regionId });
      toast.success(t("page.regionUpdated"));
      await onChanged();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "readerOperation"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reader) return;
    if (!regionId) {
      setError(t("changeRegionModal.regionRequired"));
      return;
    }
    if (regionId === reader.region_id) {
      onClose();
      return;
    }
    if (willUnpair) {
      setUnpairConfirmOpen(true);
      return;
    }
    await submitRegionChange();
  };

  return (
    <>
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen && reader != null}
      onClose={onClose}
      title={t("changeRegionModal.title")}
      size="sm"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="change-reader-region-form"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("changeRegionModal.submit")}
          </Button>
        </ModalFooter>
      }
    >
      {reader ? (
        <form
          id="change-reader-region-form"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <p className="text-sm text-surface-600">
            {t("changeRegionModal.help", { serial: serialLabel })}
          </p>
          {willUnpair ? (
            <Alert variant="warning">
              {t("changeRegionModal.unpairWarning")}
            </Alert>
          ) : null}
          <RegionSelect
            value={regionId}
            onChange={setRegionId}
            label={t("changeRegionModal.regionLabel")}
            required
            disabled={isLoading}
          />
          {error ? <Alert variant="error">{error}</Alert> : null}
        </form>
      ) : null}
    </Modal>
    <ConfirmModal
      closeLabel={tCommon("close")}
      cancelText={tCommon("cancel")}
     
      isOpen={unpairConfirmOpen}
      onClose={() => setUnpairConfirmOpen(false)}
      onConfirm={() => void submitRegionChange()}
      title={t("changeRegionModal.title")}
      message={t("changeRegionModal.unpairConfirm", { serial: serialLabel })}
      confirmText={t("changeRegionModal.submit")}
      isLoading={isLoading}
    />
    </>
  );
}
